import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ShoppingCart, Wallet, Smartphone, Globe, Paintbrush, Shield, Cpu, ArrowLeft,
  MessageSquare, MapPin, Gift, Trophy, Building2, Languages, Store,
} from "lucide-react";
import { useBranding } from "@/contexts/BrandingContext";

const phase2Items = [
  { icon: MessageSquare, title: "WhatsApp Bot Integration", desc: "Redeem vouchers and check balances via WhatsApp.", phase: "2", status: "In Progress" },
  { icon: MapPin, title: "GPS Merchant Map", desc: "Find nearby merchants on an interactive map.", phase: "2", status: "In Progress" },
  { icon: Gift, title: "Voucher Gifting", desc: "Send vouchers to friends and family.", phase: "2", status: "Planned" },
  { icon: Trophy, title: "Loyalty Programme", desc: "Earn points on every redemption.", phase: "2", status: "Planned" },
  { icon: Building2, title: "HRMS Connectors", desc: "Workday and SAP integrations for corporate payroll vouchers.", phase: "2", status: "Planned" },
  { icon: Languages, title: "Multi-Language Support", desc: "Twi, Ewe, Hausa, and Dagbani translations.", phase: "2", status: "Planned" },
  { icon: Store, title: "Agent Marketplace", desc: "Public agent directory with territory listings.", phase: "2", status: "Planned" },
];

const phase3Items = [
  { icon: ShoppingCart, title: "Voucher Marketplace", desc: "Corporate-to-corporate voucher trading.", phase: "3", status: "Planned" },
  { icon: Wallet, title: "Open Banking Top-up", desc: "Direct bank account funding via open banking.", phase: "3", status: "Planned" },
  { icon: Smartphone, title: "NFC Card Redemption", desc: "Tap-to-redeem at any POS terminal.", phase: "3", status: "Planned" },
  { icon: Globe, title: "Nigeria Expansion", desc: "NGN, NIBSS settlement, NIN identity.", phase: "3", status: "Planned" },
  { icon: Globe, title: "Kenya Expansion", desc: "KES, M-Pesa integration.", phase: "3", status: "Planned" },
  { icon: Paintbrush, title: "White-Label Licensing", desc: "Deploy VGV under your own brand.", phase: "3", status: "Planned" },
  { icon: Shield, title: "ISO 27001 Certification", desc: "Enterprise-grade security certification.", phase: "3", status: "Planned" },
  { icon: Cpu, title: "Dynamic Pricing Engine", desc: "AI-driven commission optimisation.", phase: "3", status: "Planned" },
];

const statusColors: Record<string, string> = {
  "Planned": "bg-muted text-muted-foreground",
  "In Progress": "bg-brand-gold-lt text-brand-gold",
  "Live": "bg-brand-green-lt text-brand-green-dk",
};

const RoadmapPage = () => {
  const { fullLogo } = useBranding();
  return (
  <div className="min-h-screen bg-background">
    <nav className="border-b border-border bg-card">
      <div className="container mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={fullLogo} alt="Value Gift Vouchers" className="h-14 object-contain" />
        </Link>
        <Link to="/"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Back to Home</Button></Link>
      </div>
    </nav>

    <div className="container mx-auto px-6 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-foreground mb-3">Product Roadmap</h1>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          See what's coming next for Value Gift Vouchers. Our roadmap is driven by feedback from corporates, merchants, and consumers across Ghana.
        </p>
      </div>

      <div className="mb-12">
        <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
          <Badge className="bg-brand-gold-lt text-brand-gold">Phase 2</Badge>
          Coming Soon
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {phase2Items.map((item) => (
            <Card key={item.title} className="hover:shadow-brand transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg bg-brand-gold-lt flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-brand-gold" />
                  </div>
                  <Badge className={`text-xs ${statusColors[item.status]}`}>{item.status}</Badge>
                </div>
                <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
          <Badge className="bg-brand-info-lt text-brand-info">Phase 3</Badge>
          Future Vision
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {phase3Items.map((item) => (
            <Card key={item.title} className="hover:shadow-brand transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg bg-brand-info-lt flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-brand-info" />
                  </div>
                  <Badge className={`text-xs ${statusColors[item.status]}`}>{item.status}</Badge>
                </div>
                <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  </div>
  );
};

export default RoadmapPage;
